import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const MembershipInfoSection = ({ userData }) => {
  const navigate = useNavigate();

  const tierConfig = {
    'Explorador': {
      icon: 'Compass',
      color: 'var(--color-muted-foreground)',
      bgColor: 'bg-muted',
      price: '$20.000',
      discountLimit: 15
    },
    'Emprendedor': {
      icon: 'TrendingUp',
      color: 'var(--color-accent)',
      bgColor: 'bg-accent/10',
      price: '$50.000',
      discountLimit: 50
    },
    'Elite': {
      icon: 'Crown',
      color: 'var(--color-primary)',
      bgColor: 'bg-primary/10',
      price: '$70.000',
      discountLimit: null // unlimited
    }
  };

  const currentTier = tierConfig?.[userData?.membershipTier] || tierConfig?.['Explorador'];
  const daysRemaining = Math.ceil((new Date(userData?.membershipExpiry) - new Date()) / (1000 * 60 * 60 * 24));
  const usagePercentage = currentTier?.discountLimit
    ? (userData?.discountsUsed / currentTier?.discountLimit) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Membership Overview */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-card">
        <div className="flex items-center gap-3 mb-6">
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${currentTier?.bgColor}`}>
            <Icon name={currentTier?.icon} size={24} color={currentTier?.color} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Membresía {userData?.membershipTier}</h2>
            <p className="text-sm text-muted-foreground">{currentTier?.price} COP/mes</p>
          </div>
        </div>

        {/* Expiration Info */}
        <div className="p-4 bg-muted rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Días Restantes</span>
            <span className="text-2xl font-bold text-success">{daysRemaining}</span>
          </div>
          <div className="w-full h-2 bg-background rounded-full overflow-hidden">
            <div
              className="h-full bg-success transition-all duration-500"
              style={{ width: `${(daysRemaining / 30) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Vence el {new Date(userData?.membershipExpiry)?.toLocaleDateString('es-CO', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Discount Usage */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Descuentos Utilizados</span>
            <span className="text-lg font-bold text-foreground">
              {userData?.discountsUsed}
              {currentTier?.discountLimit && ` / ${currentTier?.discountLimit}`}
            </span>
          </div>
          {currentTier?.discountLimit ? (
            <>
              <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${usagePercentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {currentTier?.discountLimit - userData?.discountsUsed} descuentos disponibles este mes
              </p>
            </>
          ) : (
            <p className="text-xs text-success mt-2 font-semibold">
              ✨ Descuentos ilimitados
            </p>
          )}
        </div>
      </div>

      {/* Membership Benefits */}
      <div className="bg-card rounded-lg border border-border p-6 shadow-card">
        <h3 className="text-sm font-semibold text-foreground mb-4">Beneficios de tu Plan</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success/10">
              <Icon name="Check" size={16} color="var(--color-success)" />
            </div>
            <span className="text-sm text-foreground">
              {currentTier?.discountLimit ? `${currentTier?.discountLimit} descuentos por mes` : 'Descuentos ilimitados'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success/10">
              <Icon name="Check" size={16} color="var(--color-success)" />
            </div>
            <span className="text-sm text-foreground">Acceso al directorio de negocios</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success/10">
              <Icon name="Check" size={16} color="var(--color-success)" />
            </div>
            <span className="text-sm text-foreground">Programa Bizznet Rewards</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success/10">
              <Icon name="Check" size={16} color="var(--color-success)" />
            </div>
            <span className="text-sm text-foreground">Soporte prioritario</span>
          </div>
        </div>
      </div>

      {/* Renewal Action */}
      {daysRemaining <= 7 && (
        <div className="bg-card rounded-lg border border-warning p-6 shadow-card">
          <div className="flex items-start gap-3 mb-4">
            <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Tu membresía está por vencer</h3>
              <p className="text-sm text-muted-foreground">
                Renueva ahora para seguir disfrutando de todos los beneficios
              </p>
            </div>
          </div>
          <Button
            variant="default"
            fullWidth
            onClick={() => navigate('/landing-page')}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Renovar Membresía
          </Button>
        </div>
      )}
    </div>
  );
};

export default MembershipInfoSection;